import { TableBody, TableCell, TableRow } from '@mui/material'
import { EnhancedTableBodyProps } from '@reader_task_list_view/reader_task_list.types'
import { getComparator, calculateTimeSpent } from '@reader_task_list_view/utils'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import ReopenModal from './ReopenModal'
import TaskStateChip from './TaskStateChip'
import TaskTypeChip from './TaskTypeChip'
import { toast } from 'sonner'
import Toast from './Toast'
import { formatTaskDate } from '@qc_task_list_view/utils'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { jwtDecode } from 'jwt-decode'
import { composeOhifReaderLink } from '@common/utils/ohifUtils'

type JwtPayload = {
  realm_access: {
    roles: string[]
  }
}

export default function EnhancedTableBodyCompletedTasks({
  rows,
  order,
  orderBy,
  authedUserToken,
}: EnhancedTableBodyProps) {
  const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows])
  const { studyId } = useParams()
  const { user } = useExtendedAuth()
  const accessToken = user?.access_token

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleRowClick = async (task_ids: string[]) => {
    if (!user?.access_token) {
      toast(<Toast message="You must be logged in to view this task." variant="error" />)
      return
    }

    const decodedToken = jwtDecode<JwtPayload>(user.access_token)
    const userRoles = decodedToken.realm_access?.roles || []
    const isAuditManager = userRoles.includes('Audit_Manager')

    if (!isAuditManager) {
      toast(<Toast message="You do not have permission to view the task after is completed." variant="error" />)
      return
    }

    if (studyId && authedUserToken && task_ids.length > 0) {
      const taskId = task_ids[0]
      openInNewTab(composeOhifReaderLink(taskId, studyId, accessToken!))
    } else {
      console.error('Missing required data for opening summary page (Completed Tasks).')
      toast(<Toast message="Cannot open summary page: Missing required information." variant="error" />)
    }
  }

  return (
    <TableBody>
      {visibleRows.map((row) => {
        return (
          <TableRow
            tabIndex={-1}
            hover
            key={row.task_id}
            onClick={() => {
              handleRowClick([row.task_id])
            }}
            sx={{
              cursor: 'pointer',
              borderBottom: '1px solid rgba(220, 227, 229, 1)',
            }}
          >
            <TableCell align="left" data-testid="task-pseudo-id-cell">
              {row.task_pseudo_id}
            </TableCell>
            <TableCell align="left" data-testid="blind-code-cell">
              {row.blind_code}
            </TableCell>
            <TableCell align="left" data-testid="timepoint-name-cell">
              {row.timepoint_pseudo_name}
            </TableCell>
            <TableCell align="left" data-testid="task-type-cell">
              <TaskTypeChip taskType={row.task_type} />
            </TableCell>
            <TableCell align="left" data-testid="priority-cell">
              {row.priority}
            </TableCell>
            <TableCell align="left" data-testid="created-at-cell">
              {formatTaskDate(row.created_at)}
            </TableCell>
            <TableCell align="left" data-testid="completed-date-cell">
              {formatTaskDate(row.completed_date ?? '')}
            </TableCell>
            <TableCell align="left" data-testid="time-spent-cell">
              {calculateTimeSpent(row.reader_active_time || 0)}
            </TableCell>
            <TableCell align="left" data-testid="status-cell">
              <TaskStateChip taskState={row.status} />
            </TableCell>
            <TableCell align="right" data-testid="reopen-cell">
              <ReopenModal read_task_id={row.task_id} read_pseudo_id={row.task_pseudo_id} study_id={studyId!} />
            </TableCell>
          </TableRow>
        )
      })}
    </TableBody>
  )
}
